import React from 'react';
import {StyleSheet, View} from 'react-native';

import Spacer from '../../../components/rewards/Spacer/Spacer';
import BackChevron from '../../../components/ui-controls/BackChevron';
import Button from '../../../components/ui-controls/Button';
import PressableWithOpacityFeedback from '../../../components/ui-controls/PressableWithOpacityFeedback';
import Text from '../../../components/ui-controls/Text';
import FormField from '../../../design-system/form/FormField';
import {layout, lineHeight, space, type} from '../../../design-system/tokens';
import theme from '../../../style/theme';
import {loginSchema} from '../../account/schemas/authSchemas';

import SignUpPageLayout, {CTA_HEIGHT, CTA_RADIUS, useWideLayout} from './SignUpPageLayout';

export const DEFAULT_HEADING = 'WELCOME BACK';
export const DEFAULT_BODY = "Good news, you're already with us! Jump right back in.";

export interface SignUpPasswordScreenContentProps {
  /** The address recognised on the previous step. Shown prefilled and locked. */
  email: string;
  /** Current value of the password field. */
  password: string;
  /** Called on every keystroke in the password field. */
  onChangePassword: (password: string) => void;
  /** Called when the enabled LOG IN button is pressed. */
  onLogIn: () => void;
  /** Called when the back arrow is pressed. */
  onBack: () => void;
  /** Called when the Forgot Password? link is pressed. */
  onForgotPassword: () => void;
  /** Renders the error chrome on the password field. */
  errorMessage?: string;
  heading?: string;
  body?: string;
}

/**
 * Presentational content for sign-up Screen 2 (existing account, password
 * entry). Props in, callbacks out: no navigation, no store access, no API.
 *
 * The brand header is not rendered here — the route supplies it. This component
 * starts at the back arrow, which sits below that header and returns the user
 * to the email step.
 *
 * The email field renders in the FormField "disabled/prefilled" state because
 * the address was already accepted on Screen 1; the only editable control on
 * this screen is the password.
 */
const SignUpPasswordScreenContent = ({
  email,
  password,
  onChangePassword,
  onLogIn,
  onBack,
  onForgotPassword,
  errorMessage,
  heading = DEFAULT_HEADING,
  body = DEFAULT_BODY,
}: SignUpPasswordScreenContentProps) => {
  const {isDesktopCard: isWide, onLayout} = useWideLayout();
  const isFormValid = loginSchema.safeParse({email, password}).success;

  const backChevron = <BackChevron onPress={onBack} testID="signUpPasswordBack" />;

  // P1 on both widths (32:2232, 537:5426), unlike the email step's mobile P2.
  const bodyText = (
    <Text style={[type.paragraph.p1, lineHeight.paragraph.p1]} align="center">
      {body}
    </Text>
  );

  const fields = (
    <>
      <FormField
        label="Email"
        required
        value={email}
        editable={false}
        testID="signUpPasswordEmail"
      />

      <Spacer size={space.s24} />

      <FormField
        label="Password"
        required
        value={password}
        onChangeText={onChangePassword}
        secureTextEntry
        visibilityToggle
        autoComplete="current-password"
        textContentType="password"
        returnKeyType="go"
        onSubmitEditing={isFormValid ? onLogIn : undefined}
        error={!!errorMessage}
        errorMessage={errorMessage}
        testID="signUpPassword"
      />

      <Spacer size={space.s24} />

      {/* Left-aligned on mobile (32:2291), centred in the desktop card (537:5490). */}
      <View style={isWide ? styles.forgotRowWide : styles.forgotRow}>
        <PressableWithOpacityFeedback
          onPress={onForgotPassword}
          accessibilityRole="button"
          accessibilityLabel="Forgot Password?"
          testID="signUpPasswordForgot">
          {/* Button/Secondary in Figma (I32:2291;26:311): type.button.secondary. */}
          <Text style={styles.forgotLabel}>Forgot Password?</Text>
        </PressableWithOpacityFeedback>
      </View>
    </>
  );

  // SHORTCUT: none - the CTA title already reads the `type.button.primary`
  // token (fontSize 14, Rubik Bold) via `ctaTitle` below, not a literal.
  const logInButton = (
    <Button
      title="LOG IN"
      onPress={onLogIn}
      disabled={!isFormValid}
      buttonStyle={styles.cta}
      disabledStyle={styles.ctaDisabled}
      titleStyle={styles.ctaTitle}
      disabledTitleStyle={styles.ctaTitleDisabled}
      testID="signUpPasswordLogIn"
    />
  );

  // Desktop (537:5420): the back control outside the card at the column's
  // left edge, then the centred card with no banner, as on the Lite Welcome
  // Back screen. The card puts a group gap between the link and LOG IN.
  if (isWide) {
    return (
      <SignUpPageLayout
        centred
        isWide={isWide}
        onLayout={onLayout}
        testID="signUpPasswordScreen"
        control={backChevron}
        heading={
          <Text style={[type.heading.h1, lineHeight.heading.h1, styles.headingWide]}>
            {heading}
          </Text>
        }
        intro={bodyText}
        main={fields}
        aside={logInButton}
      />
    );
  }

  return (
    <View style={styles.screen} onLayout={onLayout} testID="signUpPasswordScreen">
      <View style={styles.content}>
        <Spacer size={layout.mobile.stackRelated} />

        <View style={styles.backRow}>{backChevron}</View>

        <Spacer size={layout.mobile.stackRelated} />

        <View style={styles.paragraphBlock}>
          {/* SHORTCUT: `variant="heading1"` (theme) is grandfathered. The
              token equivalent, `type.heading.h1` in app/design-system/tokens,
              exists since DIS-1736 merged. Migration is deferred to avoid a
              visual change - DIS-1735 is the epic; no mass migration. */}
          <Text variant="heading1" align="center">
            {heading}
          </Text>
          {bodyText}
        </View>

        <Spacer size={layout.mobile.stackSection} />

        {fields}

        <Spacer size={layout.mobile.stackSection} />

        {logInButton}

        <Spacer size={layout.mobile.pageEnd} />
      </View>
    </View>
  );
};

export default SignUpPasswordScreenContent;

// SHORTCUT: the three narrow-column constants below have no token: the
// spacing tokens carry no gutter or column-width key. Ceiling: this screen's
// narrow layout. Upgrade path: a layout gutter/column token - DIS-1735 is the
// epic.
const GUTTER = 16;
const GUTTER_WIDE = 80;
const CONTENT_MAX_WIDTH = 600;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: theme.colour.background.general.screen,
  },
  // This screen has no hero, so unlike Screen 1 it never spans the full page
  // width: the form is centred inside a reading-width column at every size.
  content: {
    width: '100%',
    alignSelf: 'center',
    maxWidth: CONTENT_MAX_WIDTH + GUTTER_WIDE * 2,
    paddingHorizontal: GUTTER,
  },
  backRow: {
    alignItems: 'flex-start',
  },
  paragraphBlock: {
    gap: GUTTER,
  },
  // Wide only: the centred card spaces the heading and intro itself.
  headingWide: {
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
  },
  forgotRow: {
    alignItems: 'flex-start',
  },
  forgotRowWide: {
    alignItems: 'center',
  },
  forgotLabel: {
    ...type.button.secondary,
    color: theme.colour.text.link.primary,
  },
  cta: {
    width: '100%',
    height: CTA_HEIGHT,
    borderRadius: CTA_RADIUS,
    backgroundColor: theme.colour.background.button.primary,
  },
  ctaDisabled: {
    backgroundColor: theme.colour.background.button.disabled,
  },
  // @rneui's Button sets no font family, so without one the title renders in the
  // platform system stack instead of the brand face. The token carries family
  // and weight together through `resolveWeight`, the only boundary that gets
  // bold right on Android. Matches the email step's CTA, so both buttons in the
  // flow agree.
  ctaTitle: {
    ...type.button.primary,
    color: theme.colour.background.button.contrastOnPrimary,
  },
  // The theme's own disabled-label colour, used because white on the disabled
  // grey measures 1.50:1. Measured 2026-09-22: #666666 on 'lightgrey' (#D3D3D3)
  // is 3.84:1, identical across all four brands. That clears WCAG AA's 3:1 for
  // large text but not the 4.5:1 this title actually needs — bold under 18.66px
  // is normal text. Pre-existing shortfall; fixing it means darkening the
  // theme's disabled pair on every brand.
  ctaTitleDisabled: {
    color: theme.colour.text.button.disabled,
  },
});
